import React, { useState, useRef, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { Box, IconButton, Tooltip, Divider } from "@mui/material";
import { toast } from "react-toastify";
import axios from "axios";
import ReactModal from "react-modal";
import LoadingSpinner from "../../../components/LoadingSpinner";
import AddNewTicketContactModal from "../../../components/ticket/AddNewTicketContactModal";
import {
  useGetSearchByEmailQuery,
  useGetLookupByEmailQuery,
  useForwardThreadMutation,
  useGetTemplateQuery,
} from "../../../Services/email/EmailTemplate";
import { Loader } from "react-bootstrap-typeahead";
import EmailTemplateModal from "../../../components/email/EmailTemplateModal";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import ForwardIcon from "@mui/icons-material/Forward";

interface ForwardCardProps {
  otherMailDetails: any;
  mailDetails: any[];
  uuid: string;
  refetchSpecificThreads: () => void;
  refetchEmailTrail:any
  setShowForwardCard: (show: boolean) => void;
  signatureData: any;
}

const customModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "700px",
    height: "600px",
  },
};

const ForwardCard: React.FC<ForwardCardProps> = ({
  otherMailDetails,
  mailDetails,
  uuid,
  refetchSpecificThreads,
  refetchEmailTrail,
  setShowForwardCard,
  signatureData,
}) => {
  const [showCCField, setShowCCField] = useState<boolean>(false);
  const [showBccField, setShowBccField] = useState<boolean>(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [editorHtml, setEditorHtml] = useState<string>("");
  const [inputValue, setInputValue] = useState("");
  const [ccInputValue, setCcInputValue] = useState("");
  const [bccInputValue, setBccInputValue] = useState("");
  const [queryParam, setQueryParam] = useState();
  const [emailOptions, setEmailOptions] = useState<any[]>([]);
  const [lookupEmail, setLookupEmail] = useState("");
  const [emailNotPresent, setEmailNotPresent] = useState<any>("");
  const [files, setFiles] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddNewContactModalOpen, setAddNewContactModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedContact, setSelectedContact] = useState<any>();
  const [templateAttachment, setTemplateAttachment] = useState<any>();
  const [selectedTemplateDescription, setSelectedTemplateDescription] =
    useState<any>("");
  const [signature, setSignature] = useState<any>({});
  const [subject, setSubject] = useState<any>("");
  const [storeTemplateData, setStoreTemplateData] = useState<any>([]);
  const [storeNewEmailId, setStoreNewEmailId] = useState("");

  const [forwardThread, { status: forwardThreadStatus }] =
    useForwardThreadMutation();

  const { data: getTemplate } = useGetTemplateQuery({
    team_id: localStorage.getItem("teamId"),
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const apiPort = process.env.REACT_APP_API_PROTOCOL;
  const fetchingTenant = window.location.hostname.split(".")[0];

  const { data } = useGetSearchByEmailQuery(queryParam, {
    skip: !queryParam,
  });
  const { data: lookupEmailData } = useGetLookupByEmailQuery(lookupEmail, {
    skip: !lookupEmail,
  });

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "color",
    "align",
    "script",
    "background",
    "blockquote",
    "code-block",
    "header",
    "indent",
    "list",
    "script",
  ];

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }, { font: [] }],
      ["bold", "italic", "underline", "strike"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image", "blockquote", "code-block"],
      [{ color: [] }, { background: [] }, { align: [] }],
      ["clean"],
    ],
  };

  const isValidEmail = (email: string) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  };

  const openContactModal = () => {
    setAddNewContactModalOpen(true);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const afterOpenModal = () => {};

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const signatureArray = Array.isArray(signatureData)
    ? signatureData
    : signatureData
    ? [signatureData]
    : [];

  const nonDefaultSignatures = signatureArray.filter(
    (sig) => sig.is_default === false,
  );

  const extractEmailOptions = (data: any) => {
    if (!data) return [];
    return data.map((item: any) => ({ label: item.email }));
  };

  const checkEmailExists = async (email: string) => {
    try {
      const res = await axios.get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/customers/contacts/search-by-email/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
          params: { email: email.trim() },
        },
      );
      return res.data && res.data.length > 0;
    } catch (error) {
      console.error("Error checking email existence:", error);
      throw error;
    }
  };

  useEffect(() => {
    if (data) {
      setEmailOptions(extractEmailOptions(data));
    }
  }, [data]);

  useEffect(() => {
    if (getTemplate) {
      setStoreTemplateData(getTemplate);
    }
  }, [getTemplate]);

  useEffect(() => {
    if (nonDefaultSignatures.length > 0) {
      setSignature(nonDefaultSignatures);
      if (!editorHtml && !selectedTemplateDescription) {
        setEditorHtml(nonDefaultSignatures[0].signature_html);
      }
    } else {
      setSignature([]);
    }
  }, [signatureData]);

  useEffect(() => {
    if (signatureData) {
      const signatures = Array.isArray(signatureData)
        ? signatureData
        : [signatureData];
      setSignature(signatures);

      if (signatures.length > 0 && signatures[0].signature_html) {
        setEditorHtml(signatures[0].signature_html);
      }
    }
  }, [signatureData]);

  useEffect(() => {
    if (selectedTemplateDescription && selectedTemplateDescription.content) {
      setSubject(selectedTemplateDescription.subject || "");

      const signatureHtml =
        signature && signature.length > 0 ? signature[0].signature_html : "";

      const newContent = signatureHtml
        ? `${selectedTemplateDescription.content}<br/><br/>${signatureHtml}`
        : selectedTemplateDescription.content;

      setEditorHtml(newContent);
    }
  }, [selectedTemplateDescription, signature]);

  const handleInputChange = (
    event: any,
    newInputValue: string,
    setInputValue: any,
  ) => {
    if (event?.type === "click" || event?.type === "keydown") {
      setInputValue("");
      const emailOptionExists = emailOptions.some(
        (option) => option.label === newInputValue,
      );

      if (!emailOptionExists) {
        setEmailNotPresent(false);
        openContactModal();
        setStoreNewEmailId(event.target.value);
      }
    }
    if (event?.type === "change") {
      const queryParams: any = {
        email: newInputValue,
      };

      setInputValue(newInputValue);
      setQueryParam(queryParams);
    }
  };

  const handleAddEmail = (email: string, setEmails: any, emails: string[]) => {
    if (email && isValidEmail(email)) {
      if (!emails.includes(email.trim())) {
        setEmails([...emails, email.trim()]);
        setInputValue("");
      }
    }
  };

  const handleDeleteEmail = (
    emailToDelete: string,
    setEmails: any,
    emails: string[],
  ) => {
    setEmails(emails.filter((email: string) => email !== emailToDelete));
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newFiles = Array.from(event.target.files || []);
    const filePromises = newFiles.map((file: any) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const fileData = {
            file_name: file.name,
            file: e.target.result.split(",")[1],
          };
          resolve(fileData);
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });
    });

    try {
      const fileDataArray = await Promise.all(filePromises);
      setFiles((prevFiles) => [...prevFiles, ...fileDataArray]);
    } catch (error: any) {
      toast.error("An error occurred while uploading files. Please try again.");
    }
  };

  const handleDeleteFile = (fileToDelete: any) => () => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToDelete));
  };

  const handleEditorChange = (content: string) => {
    setEditorHtml(content);
  };

  const toggleCCField = () => {
    setShowCCField(!showCCField);
  };

  const toggleBccField = () => {
    setShowBccField(!showBccField);
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (
        !editorHtml ||
        editorHtml.trim() === "" ||
        editorHtml === "<p><br></p>"
      ) {
        toast.error("Text field may not be blank");
        setIsSubmitting(false);
        return;
      }

      if (!emails || emails.length === 0) {
        toast.error("Please add at least one recipient");
        setIsSubmitting(false);
        return;
      }

      const showAssignError = otherMailDetails?.action?.assignee;
      if (!showAssignError) {
        toast.error("Please assign this email");
        setIsSubmitting(false);
        return;
      }

      const allEmails = [...emails, ...cc, ...bcc].filter(
        (v, i, arr) => arr.indexOf(v) === i,
      );

      for (const email of allEmails) {
        try {
          const emailExists = await checkEmailExists(email);
          if (!emailExists) {
            setEmailNotPresent(email);
            setStoreNewEmailId(email);
            setAddNewContactModalOpen(true);
            setIsSubmitting(false);
            return;
          }
        } catch (error) {
          console.error("Error checking email:", error);
          toast.error("Failed to validate email addresses. Please try again.");
          setIsSubmitting(false);
          return;
        }
      }

      if (!otherMailDetails?.action?.disposition) {
        toast.error("Please set disposition before forwarding");
        setIsSubmitting(false);
        return;
      }

      const selectedEmailId = otherMailDetails?.id;
      if (!selectedEmailId) {
        toast.error("Email ID is missing. Please refresh and try again.");
        setIsSubmitting(false);
        return;
      }

      const queryParam = {
        contact: lookupEmailData?.id,
        original_subject: subject || "",
        to: emails,
        cc: cc,
        bcc: bcc,
        forward_note: editorHtml,
        attachments: files || [],
        template_attachments: Array.isArray(templateAttachment)
          ? templateAttachment.map((attachment) => attachment.id)
          : [],
        is_forwarded: true,
      };

      const forwardResponse = await forwardThread({
        body: queryParam,
        selectedEmailId,
      });

      if (forwardResponse?.data) {
        toast.success("Email forwarded successfully");

        setBcc([]);
        setEmails([]);
        setCc([]);
        setEditorHtml("");
        setFiles([]);
        setSubject("");
        setTemplateAttachment([]);

        await refetchSpecificThreads();
        await refetchEmailTrail();
        setShowForwardCard(false);
      } else if (forwardResponse?.error) {
        let errorMessage = "Failed to forward email. Please try again.";

        if ("data" in forwardResponse.error) {
          const errorData = forwardResponse.error.data as {
            detail?: string[] | string;
            message?: string;
          };

          if (errorData.detail) {
            errorMessage = Array.isArray(errorData.detail)
              ? errorData.detail[0]
              : errorData.detail;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        }

        console.error("Forward error:", forwardResponse.error);
        toast.error(errorMessage);
      } else {
        toast.error("Unexpected error occurred. Please try again.");
      }
    } catch (error) {
      console.error("Forward submission error:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadFile = (fileId) => {
    window.open(`${fileId}`, "_blank");
  };

  const handleDeleteAttachmentFile = (fileToDeleteId) => async () => {
    setTemplateAttachment((prev) => {
      const updatedAttachments = prev.filter(
        (file) => String(file.id) !== String(fileToDeleteId),
      );

      return updatedAttachments;
    });
  };

  const handleEmailBlur = async (
    inputValue: string,
    setEmails: any,
    emails: string[],
    setInputValue: any,
  ) => {
    if (inputValue.trim() !== "") {
      try {
        const emailExists = await checkEmailExists(inputValue.trim());
        if (!emailExists) {
          setEmailNotPresent(inputValue.trim());
          setStoreNewEmailId(inputValue.trim());
          setAddNewContactModalOpen(true);
          handleAddEmail(inputValue, setEmails, emails);
          setInputValue("");
          return;
        } else {
          handleAddEmail(inputValue, setEmails, emails);
          setInputValue("");
        }
      } catch (error) {
        console.error("Error in email blur:", error);
        toast.error("Failed to validate email address.");
      }
    }
  };

  const renderEmailField = (
    label: string,
    emails: string[],
    setEmails: any,
    inputValue: string,
    setInputValue: any,
    placeholder: string = "Add recipients",
  ) => (
    <Box sx={{ mb: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 3,
          py: 2,
          borderBottom: "1px solid #e8e8e8",
        }}
      >
        <Box
          sx={{
            minWidth: "60px",
            fontWeight: 600,
            fontSize: "14px",
            color: "#2c3e50",
          }}
        >
          {label}
        </Box>
        <Box sx={{ flex: 1, ml: 2 }}>
          <Autocomplete
            freeSolo
            multiple={false}
            options={emailOptions}
            getOptionLabel={(option: any) => option.label}
            onChange={(event, value) => {
              if (value && isValidEmail(value.label)) {
                handleAddEmail(value.label, setEmails, emails);
                setInputValue("");
              }
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) =>
              handleInputChange(event, newInputValue, setInputValue)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={emails.length === 0 ? placeholder : ""}
                size="small"
                variant="standard"
                sx={{
                  "& .MuiInput-underline:before": {
                    borderBottom: "none",
                  },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                    borderBottom: "none",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottom: "none",
                  },
                }}
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleAddEmail(inputValue, setEmails, emails);
                    setInputValue("");
                    e.preventDefault();
                  }
                }}
                onBlur={() =>
                  handleEmailBlur(inputValue, setEmails, emails, setInputValue)
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      flexWrap="wrap"
                      sx={{ gap: 0.5 }}
                    >
                      {emails?.map((email, index) => (
                        <Chip
                          key={index}
                          label={email}
                          onDelete={() =>
                            handleDeleteEmail(email, setEmails, emails)
                          }
                          size="small"
                          sx={{
                            backgroundColor: "#f0f4f8",
                            color: "#2c3e50",
                            fontWeight: 500,
                            fontSize: "13px",
                            height: "28px",
                            "& .MuiChip-deleteIcon": {
                              color: "#64748b",
                              fontSize: "18px",
                              "&:hover": {
                                color: "#ef4444",
                              },
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  ),
                }}
              />
            )}
          />
        </Box>
        {label === "To:" && (
          <Box sx={{ display: "flex", gap: 1.5, ml: 2 }}>
            <Box
              component="span"
              onClick={toggleCCField}
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: showCCField ? "#6366f1" : "#64748b",
                cursor: "pointer",
                "&:hover": {
                  color: "#4f46e5",
                },
              }}
            >
              Cc
            </Box>
            <Box
              component="span"
              onClick={toggleBccField}
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: showBccField ? "#6366f1" : "#64748b",
                cursor: "pointer",
                "&:hover": {
                  color: "#4f46e5",
                },
              }}
            >
              Bcc
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {isSubmitting ? (
        <LoadingSpinner />
      ) : (
        <Box
          sx={{
            mt: 3,
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            backgroundColor: "#ffffff",
            border: "1px solid #e8e8e8",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2.5,
              backgroundColor: "#f8fafc",
              borderBottom: "1px solid #e8e8e8",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  backgroundColor: "#f0f4ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ForwardIcon sx={{ fontSize: 18, color: "#5b7cfa" }} />
              </Box>
              <Box
                component="h3"
                sx={{
                  m: 0,
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#1a1a1a",
                }}
              >
                Forward Message
              </Box>
            </Box>
            <Tooltip title="Close">
              <IconButton
                onClick={() => setShowForwardCard(false)}
                sx={{
                  color: "#64748b",
                  "&:hover": {
                    backgroundColor: "#f1f5f9",
                    color: "#1e293b",
                  },
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Email Fields */}
          <Box>
            {renderEmailField(
              "To:",
              emails,
              setEmails,
              inputValue,
              setInputValue,
            )}
            {showCCField &&
              renderEmailField("Cc:", cc, setCc, ccInputValue, setCcInputValue)}
            {showBccField &&
              renderEmailField(
                "Bcc:",
                bcc,
                setBcc,
                bccInputValue,
                setBccInputValue,
              )}
          </Box>

          {/* Editor */}
          <Box sx={{ p: 3, mb: 5 }}>
            <ReactQuill
              theme="snow"
              value={editorHtml}
              onChange={handleEditorChange}
              modules={modules}
              formats={formats}
              style={{
                height: "250px",
                borderRadius: "8px",
              }}
            />
          </Box>

          {/* Attachments */}
          {(files.length > 0 || templateAttachment?.length > 0) && (
            <Box sx={{ px: 3, pb: 2, mt: 8 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {files.map((file, index) => (
                  <Chip
                    key={index}
                    label={file.file_name}
                    onDelete={handleDeleteFile(file)}
                    deleteIcon={<DeleteOutlineIcon />}
                    sx={{
                      backgroundColor: "#f0f4f8",
                      color: "#2c3e50",
                      fontWeight: 500,
                      fontSize: "13px",
                      height: "32px",
                      "& .MuiChip-deleteIcon": {
                        color: "#64748b",
                        "&:hover": {
                          color: "#ef4444",
                        },
                      },
                    }}
                  />
                ))}
                {templateAttachment?.map((file, index) => (
                  <Chip
                    key={index}
                    label={file.file_name}
                    onClick={() => handleDownloadFile(file.file)}
                    onDelete={handleDeleteAttachmentFile(file.id)}
                    deleteIcon={<DeleteOutlineIcon />}
                    icon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      backgroundColor: "#eef2ff",
                      color: "#6366f1",
                      fontWeight: 500,
                      fontSize: "13px",
                      height: "32px",
                      cursor: "pointer",
                      "& .MuiChip-deleteIcon": {
                        color: "#64748b",
                        "&:hover": {
                          color: "#ef4444",
                        },
                      },
                      "&:hover": {
                        backgroundColor: "#e0e7ff",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Footer */}
          <Divider />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2.5,
              backgroundColor: "#f8fafc",
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title="Attach file">
                <label htmlFor="attachFileInput">
                  <IconButton
                    component="span"
                    sx={{
                      color: "#64748b",
                      "&:hover": {
                        backgroundColor: "#f1f5f9",
                        color: "#4f46e5",
                      },
                    }}
                  >
                    <AttachFileIcon fontSize="medium" />
                  </IconButton>
                  <input
                    type="file"
                    id="attachFileInput"
                    className="d-none"
                    multiple
                    onChange={handleFileChange}
                  />
                </label>
              </Tooltip>
              <Tooltip title="Email template">
                <IconButton
                  onClick={openModal}
                  sx={{
                    color: "#64748b",
                    "&:hover": {
                      backgroundColor: "#f1f5f9",
                      color: "#4f46e5",
                    },
                  }}
                >
                  <EmailIcon fontSize="medium" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              component="button"
              onClick={handleSubmit}
              disabled={forwardThreadStatus === "pending"}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 3,
                py: 1.25,
                backgroundColor: "#6366f1",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "14px",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: "#4f46e5",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 6px -1px rgba(37, 99, 235, 0.3)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  backgroundColor: "#94a3b8",
                  cursor: "not-allowed",
                  transform: "none",
                },
              }}
            >
              {forwardThreadStatus === "pending" ? (
                <>
                  Sending... <Loader />
                </>
              ) : (
                <>
                  Send
                  <SendIcon fontSize="small" />
                </>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <ReactModal
        isOpen={isAddNewContactModalOpen}
        onRequestClose={() => setAddNewContactModalOpen(false)}
        style={customModalStyles}
        contentLabel="New Contact"
        shouldCloseOnOverlayClick={false}
      >
        <AddNewTicketContactModal
          closeModal={() => setAddNewContactModalOpen(false)}
          emailNotPresent={emailNotPresent}
          setSelectedContact={setSelectedContact}
          storeNewEmailId={storeNewEmailId}
          setEmailNotPresent={setEmailNotPresent}
        />
      </ReactModal>

      <ReactModal
        isOpen={isModalOpen}
        onAfterOpen={afterOpenModal}
        onRequestClose={closeModal}
        style={customModalStyles}
        contentLabel="Email templates"
        shouldCloseOnOverlayClick={false}
      >
        <EmailTemplateModal
          closeModal={closeModal}
          setSelectedTemplateDescription={setSelectedTemplateDescription}
          storeTemplateData={storeTemplateData}
          setTemplateAttachment={setTemplateAttachment}
        />
      </ReactModal>
    </>
  );
};

export default ForwardCard;
